import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { logoPreloader } from '../services/logoPreloader';

interface LogoContextType {
  logosLoaded: boolean;
  loadingProgress: number;
  isPreloading: boolean;
  preloadLogos: () => Promise<void>;
}

const LogoContext = createContext<LogoContextType | undefined>(undefined);

export const useLogo = () => {
  const context = useContext(LogoContext);
  if (context === undefined) {
    throw new Error('useLogo must be used within a LogoProvider');
  }
  return context;
};

interface LogoProviderProps {
  children: ReactNode;
}

export const LogoProvider: React.FC<LogoProviderProps> = ({ children }) => {
  const [logosLoaded, setLogosLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isPreloading, setIsPreloading] = useState(false);

  const preloadLogos = async () => {
    if (isPreloading || logosLoaded) return;

    setIsPreloading(true);
    setLoadingProgress(0);

    try {
      // Start preloading
      await logoPreloader.preloadAllLogos();
      
      // Monitor progress
      const progressInterval = setInterval(() => {
        const progress = logoPreloader.getLoadingProgress();
        setLoadingProgress(progress);
        
        if (progress >= 1) {
          clearInterval(progressInterval);
          setLogosLoaded(true);
          setIsPreloading(false);
        }
      }, 16); // ~60fps update

      // Fallback timeout
      setTimeout(() => {
        if (!logosLoaded) {
          clearInterval(progressInterval);
          setLogosLoaded(true);
          setIsPreloading(false);
        }
      }, 5000); // 5 second timeout

    } catch (error) {
      console.error('Error preloading logos:', error);
      setLogosLoaded(true);
      setIsPreloading(false);
    }
  };

  useEffect(() => {
    // Auto-start preloading when provider mounts
    preloadLogos();
  }, []);

  const value: LogoContextType = {
    logosLoaded,
    loadingProgress,
    isPreloading,
    preloadLogos,
  };

  return (
    <LogoContext.Provider value={value}>
      {children}
    </LogoContext.Provider>
  );
};
